import {
    Box,
    Typography,
    MenuItem,
    Select,
    FormControl,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    styled,
    Tooltip,
    FormHelperText,
} from '@mui/material';
import { Info } from '@mui/icons-material';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
}));

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    fontWeight: 'bold',
    backgroundColor: theme.palette.common.white,
}));

interface OptionsTableProps {
    optionNames: string[];
    values: Record<string, string>;
    groupOptions: Record<string, string[]>;
    onValueChange: (optionName: string, value: string) => void;
    isLegacy?: boolean;
    emptyStateText?: string;
}

export const OptionsTable = ({
    optionNames,
    values,
    groupOptions,
    onValueChange,
    isLegacy = false,
    emptyStateText = 'No options available',
}: OptionsTableProps): JSX.Element => (
    <Table stickyHeader>
        <TableHead>
            <TableRow>
                <StyledTableHeaderCell>Option name</StyledTableHeaderCell>
                <StyledTableHeaderCell>Option value</StyledTableHeaderCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {emptyStateText && optionNames.length === 0 ? (
                <TableRow>
                    <StyledTableCell colSpan={2}>
                        <Typography align="center">
                            {emptyStateText}
                        </Typography>
                    </StyledTableCell>
                </TableRow>
            ) : (
                optionNames.map((optionName) => {
                    const currentValue = values[optionName];
                    const availableValues = groupOptions[optionName] || [];
                    const hasInvalidValue = !isLegacy && currentValue
                            && !availableValues.includes(currentValue);

                    return (
                        <TableRow key={optionName} sx={isLegacy ? { opacity: 0.6 } : {}}>
                            <StyledTableCell width="50%">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography
                                        sx={isLegacy ? { color: 'text.disabled' } : {}}
                                        variant="body1"
                                    >
                                        {optionName}
                                    </Typography>
                                    {isLegacy && (
                                    <Tooltip title="This option is no longer available in the current product version but is preserved to maintain compatibility with existing configurations.">
                                        <Info sx={{
                                            ml: 1,
                                            height: 16,
                                            width: 16,
                                            color: 'text.disabled',
                                        }}
                                        />
                                    </Tooltip>
                                    )}
                                </Box>
                            </StyledTableCell>
                            <StyledTableCell width="50%">
                                <FormControl fullWidth error={!!hasInvalidValue} size="small">
                                    <Select
                                        displayEmpty
                                        disabled={isLegacy}
                                        value={currentValue || ''}
                                        onChange={(e): void => onValueChange(
                                            optionName,
                                            e.target.value,
                                        )}
                                    >
                                        <MenuItem value=""><em>Unassigned</em></MenuItem>
                                        {availableValues.map((val) => (
                                            <MenuItem key={val} value={val}>{val}</MenuItem>
                                        ))}
                                        {(isLegacy || hasInvalidValue) && currentValue && (
                                        <MenuItem value={currentValue}>{currentValue}</MenuItem>
                                        )}
                                    </Select>
                                    {hasInvalidValue && (
                                    <FormHelperText>
                                        Value is not part of current product version
                                    </FormHelperText>
                                    )}
                                </FormControl>
                            </StyledTableCell>
                        </TableRow>
                    );
                })
            )}
        </TableBody>
    </Table>
);
